import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Loader2, MapPinPlus, X } from "lucide-react";
import { useDropzone } from "react-dropzone";

import {
  activateSpatialPlan,
  ApiError,
  callFeasibility,
  CANONICAL_FAMILIES,
  fetchPlanExtractionStatus,
  fetchPlanRules,
  reviewPlanRules,
  setRuleCanonical,
  startPlanExtraction,
  updatePlanAttachment,
  uploadSpatialPlan,
  type PlanAttachment,
  type PlanAttachmentKind,
  type PlanExtractionStatus,
  type PlanUploadResponse,
  type StagedPlanRule,
} from "@/api/client";
import type { FeasibilitySummary } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { KigTierEditor } from "./KigTierEditor";
import { FeasibilityResult, toFeasibilitySummary } from "./FeasibilityResult";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BadgeStatus, type StatusKind } from "@/components/ui/badge-status";
import { FieldGroup } from "@/components/ui/section";
import { cn } from "@/lib/utils";

/**
 * Samostalni unos prostornog plana — wizard inside the feasibility panel.
 *
 * M1 koš (D4): upload N PDF-ova („ubaci sve što imaš od plana") → svaki
 * dokument dobije ulogu (korigirajuću) + opcionalno glasilo; ekstrakcija
 * čita SAMO tekst-nositelja (pročišćeni > full odredbe) → grounded
 * extraction (background, polled) → architect reviews every extracted
 * limit against its verbatim citation → approved limits are promoted and
 * the municipality appears in the selector. The architect is the review
 * gate by design (LOCKED): self-serve must not block on an admin, and
 * verifying the citation stays the profession's responsibility.
 */

const PLAN_TYPES = ["PPUG", "PPUO", "GUP", "UPU", "DPU"] as const;

/** M1 koš — user-facing role vocabulary. "Pročišćeni tekst" maps onto
 *  kind=odredbe + consolidated (decision #36) — no new kind in the model. */
const ROLE_OPTIONS: Array<{
  value: string;
  label: string;
  kind: PlanAttachmentKind;
  consolidated: boolean;
}> = [
  { value: "procisceni", label: "Pročišćeni tekst odredbi", kind: "odredbe", consolidated: true },
  { value: "odredbe", label: "Odredbe (tekstualni dio)", kind: "odredbe", consolidated: false },
  { value: "izmjene", label: "Izmjene i dopune", kind: "izmjene", consolidated: false },
  { value: "odluka", label: "Odluka", kind: "odluka", consolidated: false },
  { value: "obrazlozenje", label: "Obrazloženje", kind: "obrazlozenje", consolidated: false },
  { value: "grafika", label: "Karte (grafički dio)", kind: "grafika", consolidated: false },
  { value: "other", label: "Ostalo", kind: "other", consolidated: false },
];

function roleValueOf(att: PlanAttachment): string {
  if (att.consolidated && att.kind === "odredbe") return "procisceni";
  return att.kind;
}

function attachmentStatus(att: PlanAttachment): { kind: StatusKind; label: string } {
  if (att.is_text_carrier) return { kind: "ok", label: "iz ovoga čitam pravila" };
  if (att.kind === "grafika") return { kind: "warn", label: "spremljeno (karte — bez čitanja)" };
  if (att.kind === "izmjene" || att.content_pattern === "amendment")
    return { kind: "warn", label: "spremljeno — iz izmjena ne čitam pravila" };
  return { kind: "warn", label: "spremljeno" };
}

const LIMIT_LABELS: Array<[keyof StagedPlanRule, string]> = [
  ["kig_max", "KIG max"],
  ["kisn_max", "KISN max"],
  ["max_floors", "etaže max"],
  ["max_height_v_m", "visina v (m)"],
  ["max_total_height_v_m", "visina V (m)"],
  ["min_plot_size_m2", "min. čestica (m²)"],
  ["min_distance_reg_line_m", "min. do reg. pravca (m)"],
  ["min_distance_boundary_m", "min. do međe (m)"],
  ["min_green_area_pct", "min. zelenilo (%)"],
  ["max_building_length_m", "max. duljina (m)"],
];

function groundingMeta(rule: StagedPlanRule): { kind: StatusKind; label: string } {
  if (rule.derived) return { kind: "warn", label: "uvjetovano (+%)" };
  switch (rule.grounding_status) {
    case "grounded":
      return { kind: "ok", label: "citat potvrđen" };
    case "partial_needs_review":
      return { kind: "warn", label: "djelomično uzemljeno" };
    case "derived_needs_review":
      return { kind: "warn", label: "uvjetovano (+%)" };
    default:
      return { kind: "err", label: "bez citata" };
  }
}

type Phase =
  | "form"
  | "uploaded"
  | "extracting"
  | "review"
  | "done";

export function SpatialPlanWizard({
  onActivated,
  onClose,
  initialJlsName = "",
}: {
  /** Called with the new municipality code once the plan is live. */
  onActivated: (code: string, name: string) => void;
  onClose: () => void;
  /** R4 — prefill the JLS name (e.g. from the project's katastarska
   *  općina) so the architect who arrived via the plan-missing banner
   *  doesn't retype what the system already knows. */
  initialJlsName?: string;
}) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("form");
  const [jlsName, setJlsName] = useState(initialJlsName);
  const [planType, setPlanType] = useState<string>("PPUG");
  const [files, setFiles] = useState<File[]>([]);
  const [upload, setUpload] = useState<PlanUploadResponse | null>(null);
  const [status, setStatus] = useState<PlanExtractionStatus | null>(null);
  const [rules, setRules] = useState<StagedPlanRule[]>([]);
  const [approved, setApproved] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  // M1 koš — draft glasilo values per attachment (saved on blur).
  const [glasilaDraft, setGlasilaDraft] = useState<Record<number, string>>({});
  // M2 — 409 na uploadu: ponuda nadogradnje umjesto crvene greške.
  const [supersedeOffer, setSupersedeOffer] = useState<string | null>(null);
  // PV — provizorni verdikt nad ovim (još neaktiviranim) planom: korisnik
  // upiše zonu + tip i dobije grounded odgovor odmah, prije potvrde pravila.
  const [pvZone, setPvZone] = useState("");
  const [pvType, setPvType] = useState("individualna_stambena");
  const [pvContext, setPvContext] = useState("");
  const [pvSummary, setPvSummary] = useState<FeasibilitySummary | null>(null);
  const pollRef = useRef<number | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    // Koš: accumulate drops, dedupe by name+size so a re-drop of the
    // same PDF doesn't double it.
    onDrop: (dropped) =>
      setFiles((prev) => {
        const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
        return [...prev, ...dropped.filter((f) => !seen.has(`${f.name}::${f.size}`))];
      }),
    multiple: true,
    accept: { "application/pdf": [".pdf"] },
  });

  const uploadMutation = useMutation({
    mutationFn: (vars?: { supersede?: boolean }) => {
      if (files.length === 0) throw new Error("Ubaci barem jedan PDF plana.");
      return uploadSpatialPlan(files, jlsName, planType, vars?.supersede ?? false);
    },
    onSuccess: (resp) => {
      setError(null);
      setSupersedeOffer(null);
      setUpload(resp);
      setGlasilaDraft({});
      setPhase("uploaded");
    },
    onError: (e, vars) => {
      // M2 — pokrivena općina: 409 nije greška nego ponuda nadogradnje
      // (potvrda ponovi upload sa supersede=true; fileovi su još u stateu).
      if (e instanceof ApiError && e.status === 409 && !vars?.supersede) {
        setError(null);
        setSupersedeOffer(e.message);
        return;
      }
      setSupersedeOffer(null);
      setError(e instanceof Error ? e.message : String(e));
    },
  });

  // PV — provizorni verdikt: čita staged (još nepotvrđena) pravila ovog plana
  // preko /api/feasibility?provisional_plan_id. Općina se razrješava iz plana.
  const pvMutation = useMutation({
    mutationFn: async () => {
      if (!upload) throw new Error("Plan još nije učitan.");
      const res = await callFeasibility({
        building_type: pvType,
        designation: pvZone.trim() || null,
        context: pvContext.trim() || null,
        in_city_center: false,
        work_type: "novogradnja",
        provisional_plan_id: upload.plan_id,
      });
      return toFeasibilitySummary(res);
    },
    onSuccess: (s) => {
      setError(null);
      setPvSummary(s);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  // M1 koš — role correction / glasilo per document. The backend re-picks
  // the text carrier when a role change moves it; merge the refreshed
  // package view into the upload state.
  const patchAttachment = async (
    index: number,
    patch: { kind?: PlanAttachmentKind; consolidated?: boolean; glasilo?: string | null },
  ) => {
    if (!upload) return;
    try {
      const resp = await updatePlanAttachment(upload.plan_id, index, patch);
      setError(null);
      setUpload((u) =>
        u
          ? {
              ...u,
              attachments: resp.attachments,
              text_carrier_index: resp.text_carrier_index,
              is_consolidated: resp.is_consolidated,
              content_pattern:
                (resp.content_pattern as PlanUploadResponse["content_pattern"]) ??
                u.content_pattern,
              extraction_blocked: resp.extraction_blocked,
              candidates: resp.candidates ?? u.candidates,
            }
          : u,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const extractMutation = useMutation({
    mutationFn: () => {
      if (!upload) throw new Error("Nema učitanog plana.");
      return startPlanExtraction(upload.plan_id);
    },
    onSuccess: () => {
      setError(null);
      setPhase("extracting");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  // Poll extraction progress while the background job runs. The job is
  // LLM-bound (one agentic loop per candidate article) — minutes, not
  // seconds, so a 2.5 s poll is plenty.
  useEffect(() => {
    if (phase !== "extracting" || !upload) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await fetchPlanExtractionStatus(upload.plan_id);
        if (cancelled) return;
        setStatus(s);
        if (s.state === "done") {
          const r = await fetchPlanRules(upload.plan_id);
          if (cancelled) return;
          setRules(r.rules);
          // Default review stance: approve what the re-validator confirmed
          // verbatim; everything else starts unchecked and needs an explicit
          // human opt-in. M2 — nastavak ekstrakcije vraća OVDJE s novim
          // pravilima: postojeće korisnikove odluke se ZADRŽAVAJU (merge),
          // default vrijedi samo za nove id-jeve.
          setApproved((prev) => {
            const merged: Record<number, boolean> = {};
            for (const rule of r.rules) {
              merged[rule.id] =
                prev[rule.id] ??
                (rule.grounding_status === "grounded" && !rule.derived);
            }
            return merged;
          });
          setPhase("review");
        } else if (s.state === "error") {
          setError(s.error ?? "Ekstrakcija nije uspjela.");
          setPhase("uploaded");
        }
      } catch {
        // transient poll failure — keep trying
      }
    };
    void tick();
    pollRef.current = window.setInterval(() => void tick(), 2500);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [phase, upload]);

  const activateMutation = useMutation({
    mutationFn: async () => {
      if (!upload) throw new Error("Nema učitanog plana.");
      const approveIds = rules.filter((r) => approved[r.id] && !r.derived).map((r) => r.id);
      const rejectIds = rules.filter((r) => !approved[r.id] || r.derived).map((r) => r.id);
      if (approveIds.length === 0) {
        throw new Error("Potvrdi barem jedan limit s potvrđenim citatom.");
      }
      await reviewPlanRules(upload.plan_id, approveIds, rejectIds);
      return activateSpatialPlan(upload.plan_id);
    },
    onSuccess: (resp) => {
      setError(null);
      setPhase("done");
      void queryClient.invalidateQueries({ queryKey: ["municipalities"] });
      void queryClient.invalidateQueries({ queryKey: ["corpus-stats"] });
      onActivated(resp.municipality.code, resp.municipality.name);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const approvedCount = useMemo(
    () => rules.filter((r) => approved[r.id] && !r.derived).length,
    [rules, approved],
  );

  return (
    <div className="mt-4 rounded-lg border border-ind-200 bg-ind-50/40 p-4">
      <div className="mb-3 flex items-center gap-2">
        <MapPinPlus className="h-4 w-4 text-ind-700" />
        <h4 className="text-[0.9rem] font-bold text-stone-800">
          Ubaci sve što imaš od plana
        </h4>
        <span className="text-[0.75rem] text-stone-500">
          odredbe, izmjene, pročišćeni tekst, karte — sustav prepozna što je što,
          izvuče limite, ti ih potvrdiš.
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Zatvori"
          className="ml-auto rounded p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          {error}
        </div>
      )}

      {/* M2 — pokrivena općina: ponuda nadogradnje umjesto odbijanja. */}
      {supersedeOffer && phase === "form" && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[0.8rem] text-amber-900">
          <p>{supersedeOffer}</p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              disabled={uploadMutation.isPending}
              onClick={() => uploadMutation.mutate({ supersede: true })}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Da — učitavam noviji tekst plana (nadogradnja)
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSupersedeOffer(null)}
            >
              Odustani
            </Button>
          </div>
        </div>
      )}

      {/* M2 — trajni banner nadogradnje kroz sve faze poslije uploada. */}
      {upload?.superseding && phase !== "form" && phase !== "done" && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[0.78rem] text-amber-900">
          Nadogradnja općine <strong>{upload.superseding.municipality_name}</strong>
          {upload.superseding.previous_plan_version && (
            <> (dosadašnji plan: {upload.superseding.previous_plan_version})</>
          )}{" "}
          — postojeća pravila vrijede dok ne potvrdiš i aktiviraš nova.
        </div>
      )}

      {phase === "form" && (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <FieldGroup>
              <Label htmlFor="spw_jls" required>Općina / grad</Label>
              <Input
                id="spw_jls"
                value={jlsName}
                placeholder="npr. Lovran"
                onChange={(e) => setJlsName(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="spw_type">Tip plana</Label>
              <select
                id="spw_type"
                className="rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem]"
                value={planType}
                onChange={(e) => setPlanType(e.target.value)}
              >
                {PLAN_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </FieldGroup>
          </div>

          <div
            {...getRootProps()}
            className={cn(
              "mt-3 flex cursor-pointer items-center justify-center gap-3 rounded-md border-2 border-dashed bg-white px-4 py-5 text-center transition-colors",
              isDragActive ? "border-ind-500 bg-ind-50" : "border-stone-300 hover:border-ind-400",
            )}
          >
            <input {...getInputProps()} />
            <FileUp className="h-5 w-5 text-stone-400" />
            <span className="text-[0.85rem] text-stone-600">
              {files.length > 0
                ? `${files.length} ${files.length === 1 ? "dokument odabran" : "dokumenta odabrano"} — dodaj još ili nastavi`
                : "Povuci PDF-ove plana ili klikni za odabir (može ih biti više)"}
            </span>
          </div>

          {files.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${f.size}`}
                  className="flex items-center gap-2 rounded border border-stone-200 bg-white px-2 py-1 text-[0.8rem] text-stone-700"
                >
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="text-[0.72rem] text-stone-400">
                    {(f.size / (1024 * 1024)).toFixed(1).replace(".", ",")} MB
                  </span>
                  <button
                    type="button"
                    title="Ukloni"
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="rounded p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              disabled={uploadMutation.isPending || files.length === 0 || jlsName.trim().length < 2}
              onClick={() => uploadMutation.mutate({})}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Čitam dokumente…
                </>
              ) : (
                "Učitaj i pročitaj plan"
              )}
            </Button>
          </div>
        </>
      )}

      {phase === "uploaded" && upload && (
        <>
          <p className="text-[0.85rem] text-stone-700">
            <strong>{upload.jls_name}</strong> ({upload.plan_type}) —{" "}
            {upload.pdf_pages} str., {upload.articles_total} članaka,{" "}
            <strong>{upload.candidates.length}</strong> kandidata s limitima
            {upload.candidates.length > upload.extract_cap && (
              <> (obrađuje se prvih {upload.extract_cap})</>
            )}
            .
          </p>

          {/* M1 koš — što sam prepoznao, po dokumentu; uloga se može
              ispraviti, glasilo je neobavezno (sprema se odmah). */}
          {(upload.attachments?.length ?? 0) > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[0.78rem] font-semibold text-stone-600">
                Što sam prepoznao:
              </p>
              <ul className="flex flex-col gap-1.5">
                {(upload.attachments ?? []).map((att) => {
                  const st = attachmentStatus(att);
                  return (
                    <li
                      key={att.index}
                      className={cn(
                        "rounded-md border bg-white px-3 py-2",
                        att.is_text_carrier ? "border-green-300" : "border-stone-200",
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[0.82rem] font-semibold text-stone-800">
                          {att.naziv}
                        </span>
                        <BadgeStatus kind={st.kind}>{st.label}</BadgeStatus>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[0.76rem] text-stone-600">
                        <label className="flex items-center gap-1.5">
                          <span>uloga:</span>
                          <select
                            className="rounded border border-stone-300 bg-white px-1.5 py-0.5 text-[0.76rem]"
                            value={roleValueOf(att)}
                            onChange={(e) => {
                              const opt = ROLE_OPTIONS.find((o) => o.value === e.target.value);
                              if (opt) {
                                void patchAttachment(att.index, {
                                  kind: opt.kind,
                                  consolidated: opt.consolidated,
                                });
                              }
                            }}
                          >
                            {ROLE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="flex items-center gap-1.5">
                          <span>glasilo (neobavezno):</span>
                          <input
                            type="text"
                            placeholder="npr. SN PGŽ 4/25"
                            className="w-36 rounded border border-stone-300 bg-white px-1.5 py-0.5 text-[0.76rem]"
                            value={glasilaDraft[att.index] ?? att.glasilo ?? ""}
                            onChange={(e) =>
                              setGlasilaDraft((g) => ({ ...g, [att.index]: e.target.value }))
                            }
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (att.glasilo ?? "")) {
                                void patchAttachment(att.index, { glasilo: v || null });
                              }
                            }}
                          />
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {upload.warnings.length > 0 && (
            <ul className="mt-2 text-[0.78rem] text-amber-700">
              {upload.warnings.map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          )}
          {/* H3 — amendment gate: the upload is saved, extraction stays
              blocked until a pročišćeni/osnovni tekst is supplied. */}
          {upload.extraction_blocked ? (
            <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[0.8rem] text-amber-900">
              <strong>Nedostaje tekst odredbi za provedbu.</strong>{" "}
              Dokumenti su spremljeni, ali čitanje limita je blokirano —
              izmjene i dopune sadrže upute o izmjeni, ne važeće odredbe.
              Dodaj pročišćeni ili osnovni tekst odredbi; ako neki od
              spremljenih dokumenata to već jest, ispravi mu ulogu gore.
            </p>
          ) : (
            <p className="mt-2 text-[0.78rem] text-stone-500">
              Ekstrakcija čita kandidate člancima jedan po jedan i svaki broj
              uzemljuje doslovnim citatom — traje par minuta.
            </p>
          )}
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              disabled={
                extractMutation.isPending ||
                upload.candidates.length === 0 ||
                Boolean(upload.extraction_blocked)
              }
              onClick={() => extractMutation.mutate()}
            >
              {extractMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Izvuci limite ({Math.min(upload.candidates.length, upload.extract_cap)} članaka)
            </Button>
          </div>
        </>
      )}

      {phase === "extracting" && (
        <div className="flex items-center gap-3 py-2 text-[0.85rem] text-stone-600">
          <Loader2 className="h-4 w-4 animate-spin text-ind-600" />
          <span>
            Izvlačim limite… članak {status?.done ?? 0}/{status?.total ?? "?"} ·{" "}
            {status?.rules_staged ?? 0} pravila pronađeno
          </span>
        </div>
      )}

      {phase === "review" && (
        <>
          <p className="mb-2 text-[0.82rem] text-stone-700">
            Pregledaj izvučene limite — svaki nosi doslovni citat iz članka.
            Potvrđeni ulaze u provjeru uvjeta kao pravila ove općine
            (označeno kao korisnički unos).
          </p>
          {rules.length === 0 ? (
            <p className="text-[0.85rem] text-amber-700">
              Ekstrakcija nije pronašla nijedan limit — plan vjerojatno nema
              strojno čitljive odredbe. Javi nam se s primjerom plana.
            </p>
          ) : (
            <ul className="flex max-h-[420px] flex-col gap-2 overflow-y-auto pr-1">
              {rules.map((rule) => {
                const meta = groundingMeta(rule);
                const values = LIMIT_LABELS
                  .map(([key, label]) => {
                    const v = rule[key];
                    return v != null ? `${label}: ${String(v).replace(".", ",")}` : null;
                  })
                  .filter(Boolean);
                const spans = Object.entries(rule.value_spans ?? {});
                return (
                  <li
                    key={rule.id}
                    className={cn(
                      "rounded-md border bg-white px-3 py-2",
                      approved[rule.id] && !rule.derived
                        ? "border-green-300"
                        : "border-stone-200",
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-2 text-[0.85rem] font-semibold text-stone-800">
                        <input
                          type="checkbox"
                          disabled={rule.derived}
                          checked={Boolean(approved[rule.id]) && !rule.derived}
                          onChange={(e) =>
                            setApproved((a) => ({ ...a, [rule.id]: e.target.checked }))
                          }
                        />
                        {rule.rule_subject_code ?? "nepoznat subjekt"}
                      </label>
                      <BadgeStatus kind={meta.kind}>{meta.label}</BadgeStatus>
                      {rule.source_article && (
                        <span className="text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-ind-700">
                          čl. {rule.source_article}
                        </span>
                      )}
                    </div>
                    {/* Vocabulary review-gate: confirm/correct the canonical
                        building-type the engine will match on (residual extraction
                        errors are vocab mis-maps on novel plan naming). */}
                    <div className="mt-1 flex items-center gap-2 text-[0.74rem] text-stone-600">
                      <span>tip građevine:</span>
                      <select
                        className="rounded border border-stone-300 bg-white px-1.5 py-0.5 text-[0.74rem]"
                        value={rule.building_type_canonical ?? ""}
                        onChange={(e) => {
                          const v = e.target.value || null;
                          setRules((rs) =>
                            rs.map((r) =>
                              r.id === rule.id ? { ...r, building_type_canonical: v } : r,
                            ),
                          );
                          if (upload) void setRuleCanonical(upload.plan_id, rule.id, v);
                        }}
                      >
                        <option value="">— neodređeno —</option>
                        {CANONICAL_FAMILIES.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                      {!rule.building_type_canonical && (
                        <span className="text-amber-700">nije mapirano — odredi tip</span>
                      )}
                    </div>
                    {values.length > 0 && (
                      <p className="mt-1 text-[0.8rem] text-stone-700">
                        {values.join(" · ")}
                      </p>
                    )}
                    {spans.length > 0 && (
                      <p className="mt-1 text-[0.74rem] italic text-stone-500">
                        {spans.map(([, s]) => `„${s}”`).join(" · ")}
                      </p>
                    )}
                    {rule.derived && (
                      <p className="mt-1 text-[0.74rem] text-amber-700">
                        Uvjetovana vrijednost ({rule.applies_when ?? "+50% klauzula"}) —
                        informativno, ne ulazi u automatsku provjeru.
                      </p>
                    )}
                    {/* KIG kao funkcija površine čestice (stepenasti KIG) —
                        arhitekt kurira bandove uz citat; nudi se samo za
                        KIG-noseća pravila. */}
                    {(rule.kig_max != null || (rule.kig_tiers?.length ?? 0) > 0) && upload && (
                      <KigTierEditor
                        rule={rule}
                        planId={upload.plan_id}
                        onSaved={(fields) =>
                          setRules((rs) =>
                            rs.map((r) =>
                              r.id === rule.id
                                ? {
                                    ...r,
                                    kig_tiers: fields.kig_tiers,
                                    geo_scope: fields.geo_scope,
                                    kig_max: fields.kig_max ?? r.kig_max,
                                  }
                                : r,
                            ),
                          )
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {/* M2 — plan ima više kandidata nego što jedan krug čitanja
              obrađuje: nastavak preskače već pročitane članke. */}
          {(status?.skipped_candidates ?? 0) > 0 && (
            <div className="mt-3 rounded border border-ind-200 bg-ind-50/60 px-3 py-2 text-[0.8rem] text-stone-700">
              Još {status?.skipped_candidates} članaka s limitima čeka čitanje —
              nastavak zadržava tvoje dosadašnje potvrde.
              <Button
                type="button"
                className="ml-2"
                disabled={extractMutation.isPending}
                onClick={() => extractMutation.mutate()}
              >
                {extractMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Nastavi čitanje ({status?.skipped_candidates})
              </Button>
            </div>
          )}
          {/* PV — provizorni verdikt PRIJE potvrde: korisnik upiše zonu + tip
              i odmah dobije grounded odgovor nad ovim učitanim planom. */}
          <section className="mt-5 rounded-lg border border-ind-200 bg-ind-50/40 px-4 py-3">
            <h4 className="text-[0.85rem] font-bold text-stone-800">
              Provjeri gradivost odmah{" "}
              <span className="font-normal text-[0.75rem] text-stone-500">
                (provizorno — nad upravo učitanim planom, prije potvrde pravila)
              </span>
            </h4>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="pv-type">Što gradiš</Label>
                <select
                  id="pv-type"
                  className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-2 text-[0.85rem]"
                  value={pvType}
                  onChange={(e) => setPvType(e.target.value)}
                >
                  {CANONICAL_FAMILIES.filter((f) => f.value !== "nije_zgrada").map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="pv-zone">Zona (namjena)</Label>
                <Input
                  id="pv-zone"
                  className="mt-1"
                  placeholder="npr. M1, S1, T2"
                  value={pvZone}
                  onChange={(e) => setPvZone(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="pv-ctx">Parametri (opcionalno)</Label>
                <Input
                  id="pv-ctx"
                  className="mt-1"
                  placeholder="npr. čestica 600 m², KIG 0,35"
                  value={pvContext}
                  onChange={(e) => setPvContext(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3">
              <Button
                type="button"
                disabled={pvMutation.isPending}
                onClick={() => pvMutation.mutate()}
              >
                {pvMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Provjeri gradivost
              </Button>
            </div>
            {pvSummary && <FeasibilityResult summary={pvSummary} />}
          </section>

          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[0.78rem] text-stone-500">
              {approvedCount} od {rules.filter((r) => !r.derived).length} limita potvrđeno
            </span>
            <Button
              type="button"
              disabled={activateMutation.isPending || approvedCount === 0}
              onClick={() => activateMutation.mutate()}
            >
              {activateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {upload?.superseding
                ? "Potvrdi i zamijeni pravila općine"
                : "Potvrdi i aktiviraj općinu"}
            </Button>
          </div>
        </>
      )}

      {phase === "done" && upload && (
        <div className="flex items-center gap-2 py-2 text-[0.9rem] text-green-700">
          <CheckCircle2 className="h-5 w-5" />
          Plan je aktivan — općina je odabrana u izborniku i spremna za provjeru uvjeta.
        </div>
      )}
    </div>
  );
}
